import React, { useState, useEffect } from "react";
import {
    Plus,
    Trash2,
    FileText,
    Loader2,
    Download,
    Upload
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdminEPKServices } from "../../services/admin/AdminEPKServices";
import { useNotification } from "../../context/NotificationContext";
import ConfirmationModal from "../../components/admin/ConfirmationModal";

export default function EpkManagementPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [epk, setEpk] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    useEffect(() => {
        const fetchEPK = async () => {
            setLoading(true);
            try {
                const response = await AdminEPKServices.getEPK();
                if (response.status === "success") {
                    setEpk(response.data);
                }
            } catch (error) {
                showNotification({ message: "Failed to fetch EPK data", type: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchEPK();
    }, [showNotification]);

    const handleDelete = async () => {
        try {
            const response = await AdminEPKServices.deleteEPK();
            if (response.status === "success") {
                setEpk(null);
                showNotification({ message: "EPK deleted successfully", type: "success" });
            } else {
                showNotification({ message: response.message || "Failed to delete EPK", type: "error" });
            }
        } catch (error) {
            showNotification({ message: "An error occurred during deletion", type: "error" });
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const _formatSize = (bytes) => {
        if (!bytes) return "Unknown size";
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <>
            <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            EPK Management
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage your Electronic Press Kit document for the public press page.
                        </p>
                    </div>
                    {epk && (
                        <button
                            onClick={() => navigate("/admin/epk/upload")}
                            className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                        >
                            <Plus size={18} /> UPDATE EPK
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 size={40} className="text-jazz-brown animate-spin mb-4" />
                        <p className="text-black dark:text-white/50 text-xs font-bold tracking-widest uppercase">Loading EPK...</p>
                    </div>
                ) : epk ? (
                    <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                            <div className="w-20 h-20 bg-jazz-brown/5 rounded-2xl flex items-center justify-center shrink-0">
                                <FileText size={40} className="text-jazz-brown" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-black dark:text-white mb-1" style={{ fontFamily: "Crimson Pro, serif" }}>
                                    Current EPK Document
                                </h3>
                                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-black/60 dark:text-white/40">
                                    <span className="flex items-center gap-1.5">
                                        Last Updated: {new Date(epk.updated_at).toLocaleDateString(undefined, {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </span>
                                </div>
                                <div className="mt-4 flex gap-3">
                                    <a
                                        href={epk.file_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-zinc-800 text-black dark:text-white rounded-xl text-sm hover:bg-jazz-brown/10 hover:text-jazz-brown transition-all"
                                    >
                                        <Download size={16} /> VIEW / DOWNLOAD
                                    </a>
                                    <button
                                        onClick={() => setIsDeleteModalOpen(true)}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-500 rounded-xl text-sm hover:bg-red-500 hover:text-white transition-all font-medium"
                                    >
                                        <Trash2 size={16} /> REMOVE
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-24 text-center bg-white dark:bg-[#1A1A1A] rounded-2xl border border-dashed border-black/10 dark:border-white/10 flex flex-col items-center">
                        <div className="w-20 h-20 bg-gray-50 dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-6">
                            <Upload className="text-black/10 dark:text-white/10" size={40} />
                        </div>
                        <h3 className="text-xl font-medium text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            No EPK Uploaded Yet
                        </h3>
                        <p className="text-black/50 dark:text-white/40 text-sm max-w-md mx-auto mb-8">
                            Upload your professional Electronic Press Kit (PDF or Word) to make it available for download on the press page.
                        </p>
                        <button
                            onClick={() => navigate("/admin/epk/upload")}
                            className="flex items-center gap-2 bg-black text-white px-8 py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-lg shadow-black/10"
                        >
                            <Plus size={18} /> UPLOAD YOUR FIRST EPK
                        </button>
                    </div>
                )}
            </div>
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Remove EPK"
                message="Are you sure you want to remove the current EPK document? This will remove the download option from the press page."
            />
        </>
    );
}
